/**
 * The merge tokens a message may use — the VOCABULARY, shared by the client
 * that offers them and the server that fills them.
 *
 * One declared list, never derived from whichever fields happen to hold a
 * value: an unset field is an EMPTY token and a message using it still sends,
 * while an unknown name is UNAVAILABLE and refuses. Only a declaration can tell
 * those two apart.
 */

/** Filled from the recipient, so only a real send can resolve them. */
export const RECIPIENT_TOKENS = ['firstName', 'lastName', 'name'] as const

/**
 * The org's own facts, filled once and referenced anywhere — the same values
 * the luach prints along its head and foot.
 *
 * Being referenceable is a property a field has, not a category it belongs to,
 * so this list is the catalog and each field states its own token. The split is
 * only by where the value lives: a column on the org, or a key in its
 * `mergeFields` bag.
 */
export const ORG_FIELD_TOKENS = [
  'orgName',
  'orgNameHe',
  'rabbiLine',
  'orgAddress',
  'orgPhone',
  'orgEmail',
  'ein',
  'zelle',
  'mailingAddress',
] as const

export const MERGE_FIELD_TOKENS = ['donationLink', 'orgLegalName'] as const

/** Every token the org itself fills, in the order the settings page lists them. */
export const ORG_TOKENS = [...ORG_FIELD_TOKENS, ...MERGE_FIELD_TOKENS] as const

/** Whoever currently holds each role — assigned on Members › Staff & roles.
 *  Every role the server can produce a token for, so a role nobody holds is an
 *  EMPTY token rather than an unknown one. */
export const ROLE_TOKENS = [
  'gabbaim',
  'treasurers',
  'rov',
  'presidents',
] as const

/** What this particular send is about. Needs an event, so a message naming
 *  none cannot resolve them. */
export const EVENT_TOKENS = [
  'eventLabel',
  'eventDate',
  'eventHebrewDate',
  'sendDate',
] as const

/**
 * What this send is COLLECTING for. Needs a campaign drive, so a message naming
 * none cannot resolve them and refuses rather than sending an appeal with no
 * way to give.
 *
 * `campaignLink` is its own token rather than a second meaning for
 * `donationLink`. The two resolve to different addresses — one to the org's
 * general page, one to this year of this drive, carrying who it was sent to —
 * and overloading the general one would silently mail the wrong address on the
 * one message where attribution is the point.
 *
 * The two figures are filled from the RECIPIENT's own billing account, so like
 * the name tokens they can only be resolved by a real send.
 */
export const CAMPAIGN_TOKENS = [
  'campaignLink',
  'pledgedAmount',
  'balanceDue',
] as const

/** Every key above, mapped to an empty string — the shape of "known, but not
 *  filled in". Spread it under the real values so an unset field renders blank
 *  instead of refusing the send. */
export const blankTokens = (keys: readonly string[]): Record<string, string> =>
  Object.fromEntries(keys.map((k) => [k, '']))

/**
 * A SINGLE time, as opposed to a whole schedule block.
 *
 * `{{schedule}}` and its segments paste a rendered table. These name one
 * figure, so a sentence can read "Mincha is at 6:37" and still be computed
 * rather than typed — the one thing that keeps a notice on a door and the
 * luach on the wall from disagreeing.
 *
 * They are their own prefixes rather than an extension of `schedule.`, which
 * already means "a whole block": `schedule.mincha` beside `schedule.first_days`
 * would be two different kinds of thing spelled the same way, and a reader
 * skimming a body could not tell them apart.
 *
 * Unlike every other family here, these are NOT a declared finite list. There
 * are as many as a shul has lines, so which ones exist is a property of the
 * org rather than of this file — the declaration is the PREFIX, and a name
 * under it is resolved by looking it up.
 */
export const TIME_TOKEN_PREFIX = 'time'
export const ZMAN_TOKEN_PREFIX = 'zman'

const SERVER_OWNED_TOKENS = new Set<string>([
  ...RECIPIENT_TOKENS,
  ...ORG_FIELD_TOKENS,
  ...ROLE_TOKENS,
  ...EVENT_TOKENS,
  ...CAMPAIGN_TOKENS,
  'schedule',
])

/** Whether an org merge field would shadow a value built by the server. */
export function isReservedMessageToken(name: string): boolean {
  // The declared merge-field catalog is the intentional exception: those
  // values are org-authored text and are escaped like every other text token.
  if ((MERGE_FIELD_TOKENS as readonly string[]).includes(name)) return false
  return (
    SERVER_OWNED_TOKENS.has(name) ||
    name.startsWith('schedule.') ||
    name.startsWith(`${TIME_TOKEN_PREFIX}.`) ||
    name.startsWith(`${ZMAN_TOKEN_PREFIX}.`)
  )
}

/**
 * A davening time, addressed by `occasion_line.id`.
 *
 * The row id rather than a readable key path, because a line has no other
 * identity: `occasion_line` carries no unique constraint beyond its primary
 * key, so one rule may be placed twice on the same day and a name cannot tell
 * the two apart. A ref also survives a rename — the token keeps working and
 * the printed label follows the new wording.
 *
 * The cost is that it is unreadable and does not travel: a ref names one row in
 * one org, so it is written by the picker and never by hand, and a template
 * seeded for another shul has to resolve its own.
 */
export const timeToken = (lineId: string): string =>
  `${TIME_TOKEN_PREFIX}.${lineId}`

/** A zman, addressed by its `ZMAN_CATALOG` key — the catalog is code, so there
 *  is no row to point at and the key is stable. */
export const zmanToken = (key: string): string => `${ZMAN_TOKEN_PREFIX}.${key}`

/** The line ref a time token carries, or null if the name is not one. */
export const timeTokenRef = (name: string): string | null =>
  name.startsWith(`${TIME_TOKEN_PREFIX}.`)
    ? name.slice(TIME_TOKEN_PREFIX.length + 1) || null
    : null

/** The catalog key a zman token carries, or null if the name is not one. */
export const zmanTokenKey = (name: string): string | null =>
  name.startsWith(`${ZMAN_TOKEN_PREFIX}.`)
    ? name.slice(ZMAN_TOKEN_PREFIX.length + 1) || null
    : null
